import React, { useState, useRef, useEffect } from "react";
import { FaCheck, FaChevronDown } from "react-icons/fa";
import { useTranslation } from "react-i18next";

// Predefined business type option keys (used as values and translation keys)
const BUSINESS_TYPE_OPTIONS = [
  "Electronics",
  "Clothing & Apparel",
  "Food & Beverages",
  "Home & Garden",
  "Health & Beauty",
  "Sports & Outdoors",
  "Books & Media",
  "Toys & Games",
  "Automotive",
  "Jewelry & Accessories",
  "Pet Supplies",
  "Office Supplies",
  "Art & Crafts",
  "Baby & Kids",
  "Furniture",
  "Software & Digital Products",
  "Services",
  "Other",
];

interface BusinessTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  error?: boolean;
}

export const BusinessTypeSelector: React.FC<BusinessTypeSelectorProps> = ({
  value,
  onChange,
  className = "",
  error = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const selectedLabel = value
    ? t(`platform.onboarding.businessTypes.${value}`, value)
    : t("platform.onboarding.businessTypePlaceholder", "Select business type...");

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-4 py-3 border focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white text-left flex items-center justify-between hover:border-blue-400 transition-colors ${
          error ? "border-red-500" : "border-gray-300"
        }`}
      >
        <div className="flex-1">
          <div className={`font-semibold ${value ? "text-gray-900" : "text-gray-500"}`}>
            {selectedLabel}
          </div>
        </div>
        <FaChevronDown
          className={`ml-2 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            {BUSINESS_TYPE_OPTIONS.map((option) => {
              const optionLabel = t(`platform.onboarding.businessTypes.${option}`, option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                    value === option ? "bg-blue-50" : ""
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{optionLabel}</span>
                    {value === option && (
                      <FaCheck className="text-blue-600 text-sm" />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
